import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Box, IconButton, Paper, Switch } from "@mui/material";
import styles from "./Teams.module.scss";
import AdminHeader from "../AdminHeader";
import DataTable from "../../../components/Table";
import teamService from "../../../services/teams.service";
import { getLabel } from "../../../misc/utils.functions";
import { GridColDef } from "@mui/x-data-grid";
import { ITeamModel } from "../../../models/team.model";
import teamsService from "../../../services/teams.service";
import MainContext from "../../../context/mainContext";
import { EditIcon } from "../../../assets/icons";

const Teams: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [teams, setTeams] = useState<ITeamModel[]>([]);
  const [loading, setLoading] = useState(false);
  const { service } = useContext(MainContext);

  const handleSendByEmail = (code: string, value: boolean) => {
    service(() => teamsService.sendByEmail(code, value), {
      callback: () => {
        const changed = teams.map((o) => ({
          ...o,
          sendEmails: o.code === code ? value : o.sendEmails,
        }));
        setTeams(changed);
      },
    });
  };

  const isMembersEditable = (code: string) => {
    const notMemberEditables = [
      "STAKEHOLDER",
      "MAIN_DIVISION",
      "PEER_REVIEWERS",
    ];

    return notMemberEditables.indexOf(code) === -1;
  };

  const columns: GridColDef[] = [
    {
      field: "description",
      headerName: getLabel("teamNameRol"),
      sortable: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "membersCount",
      headerName: getLabel("members"),
      headerAlign: "center",
      align: "center",
      sortable: false,
      width: 200,
      renderCell: (params) => {
        return isMembersEditable(params.row.code) ? params.row.members : "-";
      },
    },
    {
      field: "sendEmails",
      headerName: getLabel("sendEmail"),
      sortable: false,
      headerAlign: "center",
      align: "center",
      width: 200,
      renderCell: (params) => {
        return (
          <Switch
            checked={params.row.sendEmails || false}
            onChange={(e) =>
              handleSendByEmail(params.row.code, e.target.checked)
            }
          />
        );
      },
    },
    {
      field: "actions",
      headerName: getLabel("actions"),
      headerClassName: "actions",
      sortable: false,
      cellClassName: "actions",
      width: 100,
      renderCell: (params) => {
        return isMembersEditable(params.row.code) ? (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(params.row.code);
            }}
          >
            <EditIcon />
          </IconButton>
        ) : (
          <></>
        );
      },
    },
  ];

  useEffect(() => {
    setLoading(true);

    service(() => teamService.getTeams(), {
      callback: (result) => {
        setTeams(result);
        setLoading(false);
      },
      handleError: (error: any) => {
        if ("response" in error && error.response.status === 403) {
          navigate("/unauthorized");
        }
      },
    });
  }, []);

  const handleEdit = (code: string) => {
    navigate(`/teams/${code}`);
  };

  return (
    <div>
      <AdminHeader text={t("teams")} />
      <Box className={styles.main}>
        <Paper elevation={3} className={styles.basic + " " + styles.teams}>
          <DataTable<ITeamModel>
            rows={teams}
            columns={columns}
            handleRowClick={() => {}}
            emptyMessage={t("noRowsMessage", {
              entity: t("teams"),
            })}
            autoHeight={true}
            loading={loading}
            pageSize={8}
          />
        </Paper>
      </Box>
    </div>
  );
};

export default Teams;
